import type { ApiSchemas } from '@rental/contracts/client'
import { format, formatDateTime } from '@rental/i18n'
import { Button, Field } from '@rental/ui'
import { useState } from 'react'
import { Section } from '../components/Section'
import { NamedFor, StaffInput } from '../components/ui'
import { useLocale } from '../i18n'

type OneTimePassword = ApiSchemas['OneTimePassword']
type AuthenticatorKey = ApiSchemas['AuthenticatorKey']

/**
 * S9 (Q-51): a password or a key someone else chose, shown once. It comes in
 * the answer that made it, lives in the screen's state, and goes with it:
 * the server keeps no copy to show again (no-store, no replay store), so
 * leaving the page loses it, and a new one is made from the person's page
 * (RUNBOOK, "People"). It is handed over in person, never sent: no e-mail,
 * no message from here. Each can be copied; a key is copied without the
 * spaces that group it for typing.
 */
export function ShownOnce({
  name,
  password,
  authenticator,
  onDone,
}: {
  name: string
  password: OneTimePassword | null
  authenticator: AuthenticatorKey | null
  onDone: () => void
}) {
  const { locale, messages: m } = useLocale()
  const copy = m.shownOnce
  return (
    <Section title={copy.title}>
      <p>{copy.intro}</p>
      {password ? (
        <Secret
          label={copy.password}
          value={password.password}
          copied={password.password}
          note={format(copy.passwordUntil, {
            date: formatDateTime(locale, password.expiresAt),
            name,
          })}
        />
      ) : null}
      {authenticator ? (
        <>
          <Secret
            label={copy.key}
            hint={copy.keyHint}
            value={authenticator.key}
            copied={authenticator.key.replace(/\s/g, '')}
          />
          <p className="flex flex-col gap-1">
            <a href={authenticator.otpauth} className="touch-target self-start">
              {copy.link}
            </a>
            <span className="text-sm text-text-muted">{copy.linkHint}</span>
          </p>
        </>
      ) : null}
      <div>
        <Button variant="staff" onClick={onDone}>
          {copy.done}
        </Button>
      </div>
    </Section>
  )
}

/**
 * One secret: in a field that can be read and selected but not changed, and
 * a button that copies it. Whether the copy worked is said in a line that is
 * there from the start, so it is heard when it changes.
 */
function Secret({
  label,
  hint,
  value,
  copied,
  note,
}: {
  label: string
  hint?: string
  value: string
  /** What the button puts on the clipboard. */
  copied: string
  note?: string
}) {
  const { messages: m } = useLocale()
  const [outcome, setOutcome] = useState<'copied' | 'failed' | null>(null)
  async function copy() {
    try {
      await navigator.clipboard.writeText(copied)
      setOutcome('copied')
    } catch {
      // No clipboard (an older browser, or permission refused): the field stays selectable.
      setOutcome('failed')
    }
  }
  return (
    <div className="flex flex-col gap-1">
      <Field label={label} hint={hint}>
        {(control) => (
          <div className="flex flex-wrap items-center gap-3">
            <StaffInput
              {...control}
              readOnly
              value={value}
              autoComplete="off"
              spellCheck={false}
              className="figures min-w-0 flex-1 text-lg"
              onFocus={(event) => event.currentTarget.select()}
            />
            <Button variant="outline" className="rounded-sm" onClick={() => void copy()}>
              {m.shownOnce.copy}
              <NamedFor name={label} />
            </Button>
          </div>
        )}
      </Field>
      {note ? <p className="text-sm">{note}</p> : null}
      <p role="status" className="text-sm text-text-muted">
        {outcome === 'copied'
          ? m.shownOnce.copied
          : outcome === 'failed'
            ? m.shownOnce.copyFailed
            : null}
      </p>
    </div>
  )
}
